import DOMPurify from 'dompurify'

/**
 * Sanitize HTML email body content before rendering via dangerouslySetInnerHTML.
 *
 * Inbound email bodies (Gmail/Outlook sync) and AI-drafted email previews are
 * untrusted HTML: an attacker can phish in a message body using <script>,
 * <iframe>, event handlers, or javascript: URLs to exfiltrate the agent's
 * Supabase session JWT. We run everything through DOMPurify's default HTML
 * profile before it reaches the DOM. The profile strips scripts, event
 * handlers, javascript: URLs, and other XSS vectors while preserving the
 * typical email shapes (tables, inline styles, anchors, images).
 */
// DOMPurify keeps target attributes; force rel="noopener noreferrer" on every
// anchor so a sanitized body opening in a new tab can't reach window.opener
// (reverse tabnabbing) and doesn't leak referrer to linked sites.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('rel', 'noopener noreferrer')
    if (node.getAttribute('target')) node.setAttribute('target', '_blank')
  }
})

export function sanitizeEmailHtml(html: string | null | undefined): string {
  if (!html) return ''
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } })
}

/**
 * Sanitize the HTML the ComposeEmailDialog rich-text editor produces, before
 * it is stored on email_messages / put on the wire (F8).
 *
 * The editor structurally bounds its output to the F8 vocabulary (only
 * paragraph/text/linebreak/list/link nodes are registered), but the sanitizer
 * is the enforced guarantee: whatever the editor library emits, only this
 * allow-list leaves the composer. Lexical's HTML export wraps text in styled
 * <span>s and stamps dir/class/value attributes — all stripped here (contents
 * kept). `start` is kept so a split ordered list keeps its real numbering, but
 * because this config pins a STRICT ALLOWED_URI_REGEXP, DOMPurify vets the
 * non-URI `start` VALUE against it and drops "3" unless start is URI-exempt —
 * so it needs ADD_URI_SAFE_ATTR (G1 audit F8; the sanitizeAskRizeHtml
 * precedent gets away without it only because it sets no custom URI regexp).
 * The URI regexp pins links to absolute https/http/mailto/tel, matching the
 * editor's own URL normalizer.
 *
 * SOURCE OF TRUTH pairing: supabase/functions/_shared/compose-html.ts →
 * validateComposedEmailHtml is the SERVER mirror of this allow-list (the
 * strict_html gate in send-email). That module runs under Deno and this one
 * needs a browser DOM, so the two cannot share a real import (the
 * src/lib/autonomy.ts ⇄ _shared/autonomy.ts convention). Changing the
 * vocabulary is a decision made in BOTH places.
 *
 * ALLOW_DATA_ATTR is false (RIZ171). Lexical 0.51 stamps
 * data-lexical-managed-linebreak="true" on a <br> after a trailing Shift+Enter.
 * The default (true) kept that attribute, and the server rejects any attribute
 * on <br>, so a template with a blank line could not send. data-rize-video is
 * the one data attribute this vocabulary keeps, and its value is "1", so it
 * also needs ADD_URI_SAFE_ATTR (the same trap as `start`). The server does not
 * allow the Lexical attribute either: dropLexicalLineBreakAttrs removes it
 * from <br> before the strict check, and that same string is what is sent.
 *
 * The module-level afterSanitizeAttributes hook above fires here too, so every
 * composed anchor leaves with rel="noopener noreferrer" — the server mirror
 * accepts exactly that value.
 */
export function sanitizeComposedEmailHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'ul', 'ol', 'li', 'a'],
    ALLOWED_ATTR: ['href', 'start', 'data-rize-video'],
    // `start` is a NON-URI attribute, but DOMPurify vets every allowed
    // attribute's VALUE against ALLOWED_URI_REGEXP unless the attribute is
    // URI-exempt — and our strict regexp (absolute https/http/mailto/tel only)
    // rejects "3", so <ol start="3"> silently lost its numbering (G1 audit
    // F8). ADD_URI_SAFE_ATTR exempts start from URI vetting. ADD_ATTR does NOT
    // fix this class — the attribute was already allowed; the VALUE check was
    // the stripper. data-rize-video's value is "1", so it needs the same
    // exemption or the video note marker is stripped (RIZ171).
    ADD_URI_SAFE_ATTR: ['start', 'data-rize-video'],
    // Lexical's data-lexical-managed-linebreak on <br> must not survive.
    // data-rize-video is listed above, so it is the only data attribute kept.
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: /^(?:https?:\/\/|mailto:|tel:)/i,
  })
}

/**
 * Sanitize an authored EMAIL SIGNATURE before it is stored on
 * public.user_email_signatures (mig 245).
 *
 * The composer vocabulary PLUS <img> — the one composed surface that
 * legitimately carries an image (a headshot, a brokerage logo, an
 * equal-housing badge).
 *
 * SOURCE OF TRUTH pairing: supabase/functions/_shared/compose-html.ts →
 * validateSignatureHtml is the SERVER mirror, and it is the real boundary: this
 * table is written straight through PostgREST by the browser, so what runs here
 * is UX. A signature that fails the server check is DROPPED at dispatch and the
 * mail goes out unsigned. Changing the vocabulary is a decision made in BOTH
 * places — and it must NOT be made by widening sanitizeComposedEmailHtml, which
 * travels a different path (validated at send behind strict_html) and has no
 * business carrying images.
 *
 * ⚠️ The <img> handling lives in THIS CONFIG ONLY and must never move into the
 * module-level afterSanitizeAttributes hook above: that hook also fires for
 * sanitizeEmailHtml, the INBOUND untrusted third-party renderer, whose whole
 * charter is preserving the images/tables/inline-styles real mail arrives with.
 *
 * ALLOWED_URI_REGEXP is https-only, which is STRICTER than the composer (no
 * plain http). That is deliberate and it is what keeps the client and server in
 * agreement: the server requires https for an image src, so allowing http here
 * would let someone save a signature that silently never applies. The cost is
 * that an http:// link typed into a signature is stripped; the Settings copy
 * says so.
 */
export function sanitizeEmailSignatureHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'ul', 'ol', 'li', 'a', 'img'],
    ALLOWED_ATTR: ['href', 'start', 'src', 'alt', 'width', 'height'],
    // Same non-URI-attribute trap as the composer: DOMPurify vets every allowed
    // attribute's VALUE against ALLOWED_URI_REGEXP unless it is URI-exempt, so
    // width/height/start would be stripped as "not a URL" without this.
    ADD_URI_SAFE_ATTR: ['start', 'alt', 'width', 'height'],
    ALLOWED_URI_REGEXP: /^(?:https:\/\/|mailto:|tel:)/i,
  })
}

/**
 * Sanitize the CC&R AI-answer HTML produced by renderCcrAnswer (src/lib/ccrMarkdown.ts)
 * before it reaches the DOM via dangerouslySetInnerHTML.
 *
 * The answer pipeline already escapes HTML metacharacters and only emits a tiny
 * presentation vocabulary (bold / italic / rule / line break). This is the
 * fail-closed second layer: even if that pipeline is later changed, the rendered
 * answer can never contain anything outside this allow-list — no scripts, no
 * anchors, no event handlers, no images.
 */
export function sanitizeCcrAnswerHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['strong', 'em', 'hr', 'br'],
    ALLOWED_ATTR: ['class'],
  })
}

/**
 * Sanitize the Ask Rize transcript HTML produced by renderAskRizeMarkdown
 * (src/lib/askRizeMarkdown.ts) before it reaches the DOM via
 * dangerouslySetInnerHTML.
 *
 * Same fail-closed contract as sanitizeCcrAnswerHtml, over the wider block
 * vocabulary the assistant renderer emits (paragraphs, lists, tables, code).
 * Even if that pipeline is later changed, the rendered answer can never
 * contain anything outside this allow-list — no scripts, no anchors, no event
 * handlers, no images. The anchor rel/target hook above never fires here
 * because <a> is not allowed at all (assistant links render as plain text).
 */
export function sanitizeAskRizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'strong', 'em', 'code', 'pre', 'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr', 'br', 'div'],
    // 'start' lets an ordered list that continues an enumeration render its real
    // numbers (renderAskRizeMarkdown only ever emits it as a validated integer).
    ALLOWED_ATTR: ['class', 'start'],
  })
}
